import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, onMounted, ref, watch } from 'vue';
import type { PropOptions } from 'vue';
import { DatePicker, TimePicker, Form, Radio } from 'ant-design-vue';
import { getDatepickerLocale } from '@/helpers/LocaleHelper';
import moment from 'moment';
import i18n from '@/i18n';
import type Rrule from '@/models/interfaces/Rrule';
import type { RecurrenceRuleVisibilityMap } from '@/modules/CMS/interfaces/RecurrenceRuleVisibility';


export default /*#__PURE__*/_defineComponent({
  __name: 'RecurrenceRule',
  props: {
    rrule: {
        required: true,
    } as PropOptions<Rrule> & { required: true },
    dateInputFormat: {
        default: 'HH:mm',
    },
    dateDisplayFormat: {
        default: 'DD. MM. YYYY',
    },
},
  emits: ['rruleUpdated'],
  setup(__props, { emit }) {

const props = __props;

const FormItem = Form.Item;
const RadioGroup = Radio.Group;




const { proxy } = getCurrentInstance()!;

const show = ref(false);
const frequencyOptions = ref<string[]>([]);
const rruleString = ref<string | null>(null);
const freqToShow: RecurrenceRuleVisibilityMap = {
    yearly: {
        until: true,
        interval: true,
        month: true,
        day_of_month: true,
        day_of_the_week: false,
        label: i18n.t('yearly') as string,
    },
    monthly: {
        until: true,
        interval: true,
        month: false,
        day_of_month: true,
        day_of_the_week: false,
        label: i18n.t('monthly') as string,
    },
    weekly: {
        until: true,
        interval: true,
        month: false,
        day_of_month: false,
        day_of_the_week: true,
        label: i18n.t('weekly') as string,
    },
    daily: {
        until: true,
        interval: true,
        month: false,
        day_of_month: false,
        day_of_the_week: false,
        label: i18n.t('daily') as string,
    },
    never: {
        until: false,
        interval: false,
        month: false,
        day_of_month: false,
        day_of_the_week: false,
        label: i18n.t('never') as string,
    },
};

const weekdays = [
    { label: i18n.t('Monday'), value: 'MO' },
    { label: i18n.t('Tuesday'), value: 'TU' },
    { label: i18n.t('Wednesday'), value: 'WE' },
    { label: i18n.t('Thursday'), value: 'TH' },
    { label: i18n.t('Friday'), value: 'FR' },
    { label: i18n.t('Saturday'), value: 'SA' },
    { label: i18n.t('Sunday'), value: 'SU' },
];
const months = ref<string[]>([]);
const monthDays = ref<any>({});
const untilSelected = ref('4');
const untilNum = ref<number | null>(999999);
const interval = ref(1);

const locale = computed(() => getDatepickerLocale());

function intervalFormatter(value: string) {
    const min = 0;
    const max = 365;
    const time = parseInt(value, 10);
    if (isNaN(time)) {
        return `${min}`;
    }
    if (time > max) {
        return `${max}`;
    } else if (time < min) {
        return `${min}`;
    } else {
        return value;
    }
}

function untilNumChanged(value: number | string | null) {
    if (!props.rrule) {
        return;
    }

    if (value === null) {
        props.rrule.untilNum = value;
        return;
    }

    props.rrule.untilNum = value.toString();
}

function intervalChanged(value: number | string) {
    if (!props.rrule || !value) {
        return;
    }

    props.rrule.interval = value.toString();
}

function updateStartDate(value: string) {
    if (!value || !moment(value, props.dateDisplayFormat).isValid() || !props.rrule) {
        return;
    }

    const newDate = moment(value, props.dateDisplayFormat);
    const rruleStartDate = moment(props.rrule.startDateTime);
    const rruleEndDate = moment(props.rrule.endDateTime);
    rruleStartDate
        .set('years', newDate.get('years'))
        .set('months', newDate.get('months'))
        .set('date', newDate.get('date'));
    rruleEndDate
        .set('years', newDate.get('years'))
        .set('months', newDate.get('months'))
        .set('date', newDate.get('date'));
    proxy!.$set(props.rrule, 'startDateTime', rruleStartDate);
    proxy!.$set(props.rrule, 'endDateTime', rruleEndDate);
}

function createIcalEventString({
    frequency,
    interval,
    startDateTime,
    endDateTime,
    daysOfTheMonth,
    daysOfTheWeek,
    untilDate,
    untilNum,
    month,
}: Rrule) {
    let rrule = '';
    const dateOutputFormat = 'YYYYMMDDTHHmmss[Z]';
    const frequencies = ['YEARLY', 'MONTHLY', 'WEEKLY', 'DAILY'];
    if (!startDateTime || !moment(startDateTime, props.dateInputFormat).isValid()) {
        return undefined;
    }
    if (!endDateTime || !moment(endDateTime, props.dateInputFormat).isValid()) {
        return undefined;
    }
    if (!!frequency && frequency.length > 0 && frequencies.includes(frequency.toUpperCase())) {
        rrule = `FREQ=${frequency.toUpperCase()};`;
        if (!isNaN(parseInt(interval, 10)) && parseInt(interval, 10) > 0) {
            rrule = `${rrule}INTERVAL=${interval};`;
        } else {
            rrule = '';
        }
        if (rrule.length > 0) {
            if (frequency === 'yearly' && !!month && month.length > 0) {
                const index = months.value.findIndex((monthOption) => monthOption === month);
                rrule = `${rrule}BYMONTH=${index + 1};`;
            } else if (frequency === 'yearly' && (!month || month.length === 0)) {
                rrule = '';
            }
        }
        if (rrule.length > 0) {
            if (!!daysOfTheMonth && daysOfTheMonth.length > 0) {
                rrule = `${rrule}BYMONTHDAY=${daysOfTheMonth.join(',')};`;
            } else if (!!daysOfTheWeek && daysOfTheWeek.length > 0) {
                rrule = `${rrule}BYDAY=${daysOfTheWeek.map((day) => day.slice(0, 2).toUpperCase()).join(',')};`;
            } else if (
                !!daysOfTheWeek &&
                daysOfTheWeek.length === 0 &&
                !!daysOfTheMonth &&
                daysOfTheMonth.length === 0 &&
                frequency.toLowerCase() !== 'daily'
            ) {
                rrule = '';
            }
        }
        if (rrule.length > 0) {
            if (untilDate !== undefined && moment(untilDate, 'DD. MM. YYYY').isValid()) {
                const until = moment(untilDate, 'DD. MM. YYYY').endOf('day').format(dateOutputFormat);
                rrule = `${rrule}UNTIL=${until};`;
            } else if (untilNum !== undefined && !isNaN(parseInt(untilNum ?? '', 10))) {
                const count = untilNum;
                rrule = `${rrule}COUNT=${count};`;
            }
        }
        const start = moment(startDateTime);
        rrule = `${rrule}BYHOUR=${start.get('hour')};BYMINUTE=${start.get('minute')};`;
        if (rrule.length > 0 && rrule.slice(-1) === ';') {
            rrule = rrule.slice(0, -1);
        }
    }
    return rrule;
}

function rrListToRruleString(rrule: Rrule) {
    const events = createIcalEventString(rrule);
    if (events?.length === 0) {
        return undefined;
    }
    return events;
}

function repetitionChanged(_event: Event) {
    if (untilSelected.value === '4') {
        proxy!.$set(props.rrule, 'untilNum', '999999');
        proxy!.$set(props.rrule, 'untilDate', null);
        untilNum.value = 999999;
    } else if (untilSelected.value === '3') {
        proxy!.$set(props.rrule, 'untilDate', null);
        proxy!.$set(props.rrule, 'untilNum', '1');
        untilNum.value = 1;
    } else if (untilSelected.value === '2') {
        proxy!.$set(props.rrule, 'untilNum', null);
        untilNum.value = null;
    }
}

function rruleChanged(newData: Rrule) {
    if (!props.rrule) {
        return;
    }

    untilNum.value = props.rrule.untilNum ? parseInt(props.rrule.untilNum, 10) : null;
    interval.value = parseInt(props.rrule.interval, 10);
    const newRrule = { ...props.rrule };
    if (!newData.startDateTime || !newData.endDateTime) {
        return;
    }
    if (newRrule.untilNum && newRrule.untilNum === '999999') {
        untilSelected.value = '4';
        newRrule.untilDate = null;
    } else if (newRrule.untilNum) {
        untilSelected.value = '3';
        newRrule.untilDate = null;
    } else if (newRrule.untilDate) {
        untilSelected.value = '2';
    }
    rruleString.value = rrListToRruleString(newRrule) ?? null;
    emit('rruleUpdated', rruleString.value);
}

function frequencyChanged(newVal: string, oldVal: string) {
    if (!props.rrule) {
        return;
    }

    if (newVal !== oldVal) {
        const showable = freqToShow[newVal];
        if (!showable?.until) {
            props.rrule.untilDate = null;
            props.rrule.untilNum = null;
            untilNum.value = null;
            untilSelected.value = '1';
        }
        if (!showable?.interval) {
            props.rrule.interval = '1';
        }
        if (!showable?.month) {
            props.rrule.month = '';
        }
        if (!showable?.day_of_month) {
            props.rrule.daysOfTheMonth
                ? props.rrule.daysOfTheMonth.splice(0, props.rrule.daysOfTheMonth.length)
                : props.rrule.daysOfTheMonth;
        }
        if (!showable?.day_of_the_week) {
            props.rrule.daysOfTheWeek
                ? props.rrule.daysOfTheWeek.splice(0, props.rrule.daysOfTheWeek.length)
                : props.rrule.daysOfTheWeek;
        }
    }
}

watch(() => props.rrule, rruleChanged, { deep: true, immediate: true });
watch(() => props.rrule.frequency, frequencyChanged);

onMounted(() => {
    frequencyOptions.value = Object.keys(freqToShow);
    const thirty1 = Array.from({ length: 31 }, (_value, index) => index + 1);
    const twenty9 = thirty1.slice(0, -2);
    const thirty = thirty1.slice(0, -1);
    monthDays.value = {
        January: thirty1,
        February: twenty9,
        March: thirty1,
        April: thirty,
        May: thirty1,
        June: thirty,
        July: thirty1,
        August: thirty1,
        September: thirty,
        October: thirty1,
        November: thirty,
        December: thirty1,
    };
    months.value = Object.keys(monthDays.value);

    if (!props.rrule) {
        return;
    }

    props.rrule.untilNum = props.rrule.untilNum ?? '999999';
    untilNum.value = parseInt(props.rrule.untilNum, 10);
    props.rrule.interval = props.rrule.interval ?? '1';
    interval.value = parseInt(props.rrule.interval, 10);
});

return { __sfc: true,FormItem, RadioGroup, props, emit, proxy, show, frequencyOptions, rruleString, freqToShow, weekdays, months, monthDays, untilSelected, untilNum, interval, locale, intervalFormatter, untilNumChanged, intervalChanged, updateStartDate, createIcalEventString, rrListToRruleString, repetitionChanged, rruleChanged, frequencyChanged, DatePicker, TimePicker, Radio, moment }
}

})